/**
 * AI Governance — Lenavio audit log for every Claude call.
 *
 * Renders three things:
 *   - Summary cards (total calls, total cost, error rate, reviewed %)
 *   - Filter pills (feature, status, reviewed)
 *   - Paginated table of AI audit rows with expand-to-view summaries
 *     and a "Mark reviewed" toggle per row.
 *
 * Plan-gated by ``audit_logs`` server-side. The parent ``AuditLogPage``
 * only mounts this tab for trialing/Enterprise users.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  DollarSign,
  Download,
  Loader2,
  Sparkles,
} from "lucide-react";

import {
  fetchAIAuditSummary,
  listAIAudit,
  markAIAuditReviewed,
  type AIAuditFilters,
  type AIAuditRow,
  type AIAuditSummary,
} from "@/api/aiAudit";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useToast } from "@/hooks/useToast";

const FEATURES = [
  "candidate_scoring",
  "jd_generator",
  "talent_assessment",
  "succession_recommend",
  "comp_benchmark",
  "flight_risk",
  "workforce_impact",
  "workforce_scenario",
  "pulse_analysis",
  "talent_rediscovery",
  "ai_assistant",
  "ai_generative",
  "maya_demo",
  "atlas",
  "sentinel",
  "pilot",
];

function fmtUsd(n: number): string {
  if (n < 0.01) return `$${n.toFixed(4)}`;
  return `$${n.toFixed(2)}`;
}

function fmtTokens(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return String(n);
}

function downloadCsv(filename: string, rows: string[][]) {
  const csv = rows
    .map((r) =>
      r
        .map((cell) => {
          const s = (cell ?? "").toString();
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
        })
        .join(","),
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function AIGovernanceTab() {
  const { addToast } = useToast();
  const [summary, setSummary] = useState<AIAuditSummary | null>(null);
  const [filters, setFilters] = useState<AIAuditFilters>({
    page: 1,
    per_page: 25,
  });
  const [rows, setRows] = useState<AIAuditRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyRow, setBusyRow] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [page, sum] = await Promise.all([
        listAIAudit(filters),
        fetchAIAuditSummary(30),
      ]);
      setRows(page.rows);
      setTotal(page.total);
      setSummary(sum);
    } catch {
      addToast("error", "Failed to load AI audit log");
    } finally {
      setLoading(false);
    }
  }, [filters, addToast]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  async function onToggleReviewed(row: AIAuditRow) {
    setBusyRow(row.id);
    try {
      const res = await markAIAuditReviewed(row.id, !row.human_reviewed);
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
                ...r,
                human_reviewed: res.human_reviewed,
                reviewed_at: res.reviewed_at,
                reviewed_by: res.reviewed_by,
              }
            : r,
        ),
      );
    } catch {
      addToast("error", "Could not update review status");
    } finally {
      setBusyRow(null);
    }
  }

  function onExport() {
    const header = [
      "Timestamp",
      "User",
      "Feature",
      "Model",
      "Tokens in",
      "Tokens out",
      "Cost USD",
      "Duration ms",
      "Status",
      "Reviewed",
    ];
    const data = rows.map((r) => [
      r.created_at,
      r.user_name || r.user_id || "",
      r.feature,
      r.model_used,
      String(r.tokens_input),
      String(r.tokens_output),
      r.cost_estimate_usd.toFixed(6),
      String(r.duration_ms),
      r.status,
      r.human_reviewed ? "yes" : "no",
    ]);
    downloadCsv("ai-audit-log.csv", [header, ...data]);
  }

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(total / (filters.per_page || 25))),
    [total, filters.per_page],
  );

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <SummaryCard
          icon={<Sparkles className="h-4 w-4" />}
          label="AI calls (30d)"
          value={summary ? String(summary.total_calls) : "—"}
        />
        <SummaryCard
          icon={<DollarSign className="h-4 w-4" />}
          label="Total cost (30d)"
          value={summary ? fmtUsd(summary.total_cost_usd) : "—"}
        />
        <SummaryCard
          icon={<CheckCircle2 className="h-4 w-4 text-success" />}
          label="Human-reviewed"
          value={
            summary ? `${summary.human_reviewed_pct.toFixed(1)}%` : "—"
          }
        />
        <SummaryCard
          icon={<AlertCircle className="h-4 w-4 text-warning" />}
          label="Error rate"
          value={
            summary ? `${summary.error_rate_pct.toFixed(1)}%` : "—"
          }
        />
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-xs text-text-muted">
            Feature
            <select
              className="mt-1 block rounded-md border border-border bg-background px-2 py-1 text-sm"
              value={filters.feature || ""}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  feature: e.target.value || undefined,
                  page: 1,
                })
              }
            >
              <option value="">All</option>
              {FEATURES.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
          </label>
          <label className="text-xs text-text-muted">
            Status
            <select
              className="mt-1 block rounded-md border border-border bg-background px-2 py-1 text-sm"
              value={filters.status || ""}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  status: e.target.value || undefined,
                  page: 1,
                })
              }
            >
              <option value="">All</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
            </select>
          </label>
          <label className="text-xs text-text-muted">
            Reviewed
            <select
              className="mt-1 block rounded-md border border-border bg-background px-2 py-1 text-sm"
              value={
                filters.reviewed === undefined
                  ? ""
                  : filters.reviewed
                    ? "yes"
                    : "no"
              }
              onChange={(e) =>
                setFilters({
                  ...filters,
                  reviewed:
                    e.target.value === ""
                      ? undefined
                      : e.target.value === "yes",
                  page: 1,
                })
              }
            >
              <option value="">All</option>
              <option value="yes">Reviewed</option>
              <option value="no">Not reviewed</option>
            </select>
          </label>
          <label className="text-xs text-text-muted">
            Since
            <input
              type="date"
              className="mt-1 block rounded-md border border-border bg-background px-2 py-1 text-sm"
              value={filters.since?.slice(0, 10) || ""}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  since: e.target.value || undefined,
                  page: 1,
                })
              }
            />
          </label>
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" size="sm" onClick={onExport}>
              <Download className="mr-1 h-4 w-4" /> CSV
            </Button>
          </div>
        </div>
      </Card>

      <Card>
        {loading ? (
          <div className="flex h-32 items-center justify-center text-text-muted">
            <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading…
          </div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-text-muted">
            No AI calls match the current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-muted text-text-muted">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-left">User</th>
                  <th className="px-3 py-2 text-left">Feature</th>
                  <th className="px-3 py-2 text-left">Model</th>
                  <th className="px-3 py-2 text-right">Tokens</th>
                  <th className="px-3 py-2 text-right">Cost</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-center">Reviewed</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const isOpen = !!expanded[r.id];
                  return (
                    <>
                      <tr
                        key={r.id}
                        className="cursor-pointer border-t border-border hover:bg-surface-muted/40"
                        onClick={() =>
                          setExpanded((p) => ({ ...p, [r.id]: !p[r.id] }))
                        }
                      >
                        <td className="px-3 py-2 text-text-muted">
                          {isOpen ? (
                            <ChevronDown className="mr-1 inline h-3.5 w-3.5" />
                          ) : (
                            <ChevronRight className="mr-1 inline h-3.5 w-3.5" />
                          )}
                          {new Date(r.created_at).toLocaleString()}
                        </td>
                        <td className="px-3 py-2 text-text-muted">
                          {r.user_name || "—"}
                        </td>
                        <td className="px-3 py-2 font-medium">{r.feature}</td>
                        <td className="px-3 py-2 text-text-muted">
                          {r.model_used}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {fmtTokens(r.tokens_input + r.tokens_output)}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums">
                          {fmtUsd(r.cost_estimate_usd)}
                        </td>
                        <td className="px-3 py-2">
                          <Badge
                            variant={
                              r.status === "success" ? "success" : "danger"
                            }
                          >
                            {r.status}
                          </Badge>
                        </td>
                        <td
                          className="px-3 py-2 text-center"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            disabled={busyRow === r.id}
                            checked={r.human_reviewed}
                            onChange={() => onToggleReviewed(r)}
                          />
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="border-t border-border bg-surface-muted/30">
                          <td colSpan={8} className="px-4 py-3">
                            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                              <div>
                                <div className="mb-1 text-xs uppercase tracking-wide text-text-muted">
                                  Input (truncated)
                                </div>
                                <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-surface p-2 text-xs">
                                  {r.input_summary || "—"}
                                </pre>
                              </div>
                              <div>
                                <div className="mb-1 text-xs uppercase tracking-wide text-text-muted">
                                  Output (truncated)
                                </div>
                                <pre className="max-h-40 overflow-auto whitespace-pre-wrap rounded-md border border-border bg-surface p-2 text-xs">
                                  {r.output_summary ||
                                    r.error_message ||
                                    "—"}
                                </pre>
                              </div>
                            </div>
                            <div className="mt-2 text-xs text-text-muted">
                              Duration: {r.duration_ms} ms · Tokens in:{" "}
                              {r.tokens_input} · Tokens out: {r.tokens_output}
                              {r.reviewed_by_name && (
                                <>
                                  {" "}
                                  · Reviewed by {r.reviewed_by_name}
                                  {r.reviewed_at &&
                                    ` on ${new Date(r.reviewed_at).toLocaleString()}`}
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {!loading && total > (filters.per_page || 25) && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <span className="text-text-muted">
            Page {filters.page || 1} of {totalPages} · {total} rows
          </span>
          <Button
            variant="ghost"
            size="sm"
            disabled={(filters.page || 1) <= 1}
            onClick={() =>
              setFilters({ ...filters, page: (filters.page || 1) - 1 })
            }
          >
            Prev
          </Button>
          <Button
            variant="ghost"
            size="sm"
            disabled={(filters.page || 1) >= totalPages}
            onClick={() =>
              setFilters({ ...filters, page: (filters.page || 1) + 1 })
            }
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-text-muted">
        {icon}
        {label}
      </div>
      <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
    </Card>
  );
}

export default AIGovernanceTab;
